/**
 * MarqueeSelection - 框选组件
 * 复刻 trial_demo_editor 的 MarqueeSelection.jsx
 * 在背景区域按下鼠标拖出选择框，通过 AABB 碰撞检测选中 block
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';

interface SelectionBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface MarqueeSelectionProps {
  onSelectionChange: (ids: string[]) => void;
}

export function MarqueeSelection({ onSelectionChange }: MarqueeSelectionProps) {
  const [selectionBox, setSelectionBox] = useState<SelectionBox | null>(null);
  const isDragging = useRef(false);
  const startPoint = useRef({ x: 0, y: 0 });
  const lastMouse = useRef({ x: 0, y: 0 });
  const rafRef = useRef<number | null>(null);
  // 记录框选开始时滚动容器的 scrollTop，用于滚动补偿
  const scrollParentRef = useRef<HTMLElement | null>(null);
  const startScrollTop = useRef(0);
  // 编辑区容器边界，用于裁剪选择框
  const containerBoundsRef = useRef<{ left: number; top: number; right: number; bottom: number } | null>(null);

  const checkCollision = useCallback((boxRect: { left: number; top: number; right: number; bottom: number }) => {
    const blocks = document.querySelectorAll('[data-block-id]');
    const selectedIds: string[] = [];

    blocks.forEach(el => {
      const rect = el.getBoundingClientRect();
      if (
        rect.left < boxRect.right &&
        rect.right > boxRect.left &&
        rect.top < boxRect.bottom &&
        rect.bottom > boxRect.top
      ) {
        const id = el.getAttribute('data-block-id');
        if (id) selectedIds.push(id);
      }
    });

    onSelectionChange(selectedIds);
  }, [onSelectionChange]);

  useEffect(() => {
    // 找到最近的可滚动父元素
    const findScrollParent = (el: HTMLElement | null): HTMLElement | null => {
      while (el) {
        const style = getComputedStyle(el);
        if (style.overflowY === 'auto' || style.overflowY === 'scroll') return el;
        el = el.parentElement;
      }
      return null;
    };

    const updateSelectionBox = (currentX: number, currentY: number) => {
      // 计算滚动偏移补偿：滚动后 startPoint 对应的视口位置会变
      const sp = scrollParentRef.current;
      let scrollDelta = 0;
      if (sp) {
        scrollDelta = sp.scrollTop - startScrollTop.current;
      }
      // startPoint 是开始时的 clientY，向下滚动后起始点在视口中"上移"了
      const adjustedStartY = startPoint.current.y - scrollDelta;
      const startX = startPoint.current.x;

      const x = Math.min(startX, currentX);
      const y = Math.min(adjustedStartY, currentY);
      const width = Math.abs(currentX - startX);
      const height = Math.abs(currentY - adjustedStartY);
      setSelectionBox({ x, y, width, height });
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        checkCollision({ left: x, top: y, right: x + width, bottom: y + height });
      });
    };

    const handleMouseDown = (e: MouseEvent) => {
      // 如果点击的是拖拽手柄，不启动框选
      if ((e.target as HTMLElement).closest('[data-drag-handle]')) return;

      // 如果点击在 block 内部（包括 contentEditable、bullet 等），不启动框选
      if ((e.target as HTMLElement).closest('[data-block-id]')) return;

      // 只有点击背景区域才触发
      const target = e.target as HTMLElement;
      if (target.dataset.isBackground) {
        isDragging.current = true;
        startPoint.current = { x: e.clientX, y: e.clientY };
        lastMouse.current = { x: e.clientX, y: e.clientY };
        setSelectionBox({ x: e.clientX, y: e.clientY, width: 0, height: 0 });
        onSelectionChange([]);
        // 找到滚动容器并记录初始 scrollTop
        const sp = findScrollParent(target);
        scrollParentRef.current = sp;
        startScrollTop.current = sp ? sp.scrollTop : 0;
        // 记录滚动容器的视口边界，用于裁剪选择框
        if (sp) {
          const r = sp.getBoundingClientRect();
          containerBoundsRef.current = { left: r.left, top: r.top, right: r.right, bottom: r.bottom };
        } else {
          containerBoundsRef.current = null;
        }

        e.preventDefault();
      }
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging.current) return;
      lastMouse.current = { x: e.clientX, y: e.clientY };
      updateSelectionBox(e.clientX, e.clientY);
    };

    // 框选过程中滚轮：手动滚动容器并重新计算选区
    const handleWheel = (e: WheelEvent) => {
      if (!isDragging.current || !scrollParentRef.current) return;
      scrollParentRef.current.scrollTop += e.deltaY;
      e.preventDefault();
      updateSelectionBox(lastMouse.current.x, lastMouse.current.y);
    };

    const handleMouseUp = () => {
      if (isDragging.current) {
        isDragging.current = false;
        setSelectionBox(null);
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
        scrollParentRef.current = null;
      }
    };

    window.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('wheel', handleWheel, { passive: false });
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('wheel', handleWheel);
      window.removeEventListener('mouseup', handleMouseUp);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [onSelectionChange, checkCollision]);

  if (!selectionBox) return null;

  // 裁剪选择框到编辑区容器范围内
  const bounds = containerBoundsRef.current;
  let clipped = selectionBox;
  if (bounds) {
    const left = Math.max(selectionBox.x, bounds.left);
    const top = Math.max(selectionBox.y, bounds.top);
    const right = Math.min(selectionBox.x + selectionBox.width, bounds.right);
    const bottom = Math.min(selectionBox.y + selectionBox.height, bounds.bottom);
    clipped = {
      x: left,
      y: top,
      width: Math.max(0, right - left),
      height: Math.max(0, bottom - top),
    };
  }

  return ReactDOM.createPortal(
    <div
      style={{
        position: 'fixed',
        left: clipped.x,
        top: clipped.y,
        width: clipped.width,
        height: clipped.height,
        backgroundColor: 'rgba(35, 131, 226, 0.1)',
        border: '1px solid rgba(35, 131, 226, 0.5)',
        borderRadius: '4px',
        pointerEvents: 'none',
        zIndex: 9999,
      }}
    />,
    document.body
  );
}

export default MarqueeSelection;
